import { useCallback, useEffect, useRef, useState } from 'react'
import { subYears } from 'date-fns'
import DateTimePicker from 'react-datetime-picker'
import clsx from 'clsx'

interface ResponsiveDatePickerProps {
	value: Date | null | undefined
	onChange: (value: Date | null) => void
	onBlur?: () => void
	ariaLabel?: string
	minAge?: number
	className?: string
	isDirty?: boolean
	disableCalendar?: boolean
	defaultActiveStartDate?: Date | null
	/** Which edge of the field the calendar lines up with. `end` suits narrow fields, `start` wide ones. */
	align?: 'start' | 'end'
}

// react-calendar's default width; the left-aligned popup is clamped so it never runs off the right edge.
const CALENDAR_WIDTH = 350
const VIEWPORT_MARGIN = 16

export const ResponsiveDatePicker = ({
	value,
	onChange,
	onBlur,
	ariaLabel,
	minAge = 14,
	className,
	isDirty,
	disableCalendar = false,
	defaultActiveStartDate,
	align = 'end',
}: ResponsiveDatePickerProps) => {
	const [pickerReady, setPickerReady] = useState(false)
	const [activeStartDate, setActiveStartDate] = useState<Date | undefined>(value ?? undefined)
	const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		// Delay rendering DateTimePicker to avoid hydration issues
		const timer = setTimeout(() => setPickerReady(true), 100)
		return () => clearTimeout(timer)
	}, [])

	useEffect(() => {
		if (!pickerReady || disableCalendar) return

		// Create a fixed-position portal div so the calendar escapes any
		// overflow:hidden / overflow-y:auto ancestor (e.g. inside a modal).
		// The `custom-react-datetime-picker` class carries the site's calendar
		// styling; keep it on the portal so styles reach the portaled calendar.
		const div = document.createElement('div')
		div.className = clsx('custom-react-datetime-picker', align === 'start' && 'align-start')
		div.style.position = 'fixed'
		div.style.zIndex = '9999'
		document.body.appendChild(div)
		setPortalContainer(div)

		return () => {
			document.body.removeChild(div)
		}
	}, [pickerReady, disableCalendar, align])

	// `end` (default): right-align the portal to the input's right edge — the calendar flows to the left
	// from there, which keeps it on screen since the input's right edge is always within the viewport.
	// `start`: line it up with the input's left edge, clamped so it stays within the viewport.
	const handleCalendarOpen = useCallback(() => {
		if (!portalContainer || !wrapperRef.current) return
		const rect = wrapperRef.current.getBoundingClientRect()
		portalContainer.style.top = `${rect.bottom + 2}px`
		if (align === 'start') {
			const maxLeft = window.innerWidth - CALENDAR_WIDTH - VIEWPORT_MARGIN
			portalContainer.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft))}px`
			portalContainer.style.right = 'auto'
		} else {
			portalContainer.style.left = 'auto'
			portalContainer.style.right = `${window.innerWidth - rect.right}px`
		}
	}, [portalContainer, align])

	const maxDate = subYears(new Date(), minAge)

	const baseInputClassName = 'px-4 py-3 border-2 rounded-lg w-full h-12'
	const defaultClassName =
		'border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg text-primary-text'

	const inputClassName = clsx(baseInputClassName, className || defaultClassName, isDirty && 'border-primary')

	if (!pickerReady) {
		return (
			<div className="custom-react-datetime-picker">
				<div className={clsx(inputClassName, 'flex items-center text-secondary-text italic font-light')}>
					mm/dd/yyyy
				</div>
			</div>
		)
	}

	return (
		<div ref={wrapperRef} className="custom-react-datetime-picker">
			<DateTimePicker
				className={clsx(
					inputClassName,
					'flex items-center placeholder-secondary-text placeholder:italic placeholder:font-light'
				)}
				value={value}
				onChange={(date: Date | null) => {
					onChange(date)
					if (date) setActiveStartDate(date)
				}}
				onBlur={onBlur}
				format="MM/dd/yyyy"
				// The picker is three inputs, so a label's htmlFor cannot reach it; name its parts instead.
				nativeInputAriaLabel={ariaLabel}
				dayAriaLabel={ariaLabel && `${ariaLabel}, day`}
				monthAriaLabel={ariaLabel && `${ariaLabel}, month`}
				yearAriaLabel={ariaLabel && `${ariaLabel}, year`}
				monthPlaceholder="mm"
				dayPlaceholder="dd"
				yearPlaceholder="yyyy"
				disableClock
				maxDate={maxDate}
				calendarIcon={null}
				clearIcon={null}
				disableCalendar={disableCalendar}
				portalContainer={portalContainer ?? undefined}
				onCalendarOpen={handleCalendarOpen}
				calendarProps={{
					...(defaultActiveStartDate && {
						defaultActiveStartDate,
					}),
					activeStartDate: activeStartDate,
					onActiveStartDateChange: ({ activeStartDate: d }) => d && setActiveStartDate(d),
					calendarType: 'gregory',
				}}
			/>
		</div>
	)
}

export default ResponsiveDatePicker
